import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useUser } from "../context/UserContext";
import { WorkflowRunCase } from "./useWorkflowRunInstance";
import { logAuditTrace } from "../services/auditNavigationTraceService";
import { ModuleNames } from "../data/constants";
import { getCurrentESTTime } from "../utils/dateHelper";

/** The navigation-trace object type for a workflow case. */
export const WORKFLOW_CASE_TRACE_TYPE = "WorkflowCase";
const TRACE_ACTION = "Navigated";

/**
 * Decision 6.3: the Run screen's navigation trace for a real case — one entry when the case is
 * opened (module, route, RefNo; the user and Eastern time are the trace's own fields) and one for
 * each stage the user lands on. The engine timeline (System_WorkflowInstanceEvents) records what
 * changed; this records what was looked at. Nothing is written until there is a case.
 *
 * A stage is traced when the case's current stage changes, not on every reload of the same stage;
 * logAuditTrace also drops an identical entry within a minute.
 */
const useWorkflowRunTrace = (runCase: WorkflowRunCase | null) => {
  const { userId } = useUser();
  const location = useLocation();
  const route = `${location.pathname}${location.search}`;
  const openedRef = useRef<number | null>(null);
  const landedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!runCase || !userId) return;
    const caseLabel = runCase.refNo || `#${runCase.instanceId}`;
    const trace = (objectName: string) =>
      logAuditTrace({
        userId,
        objectType: WORKFLOW_CASE_TRACE_TYPE,
        objectId: runCase.instanceId,
        objectName,
        action: TRACE_ACTION,
        navigationDateTime: getCurrentESTTime(),
      });

    if (openedRef.current !== runCase.instanceId) {
      openedRef.current = runCase.instanceId;
      landedRef.current = null;
      trace(`Opened workflow case ${caseLabel} (${ModuleNames.WorkflowInstances}, ${route})`);
    }

    const landedKey = `${runCase.instanceId}:${runCase.currentStageId ?? ""}`;
    if (runCase.currentStageId && landedRef.current !== landedKey) {
      landedRef.current = landedKey;
      const stage = runCase.definition.canvases.find((canvas) => canvas.id === runCase.currentStageId);
      trace(`Landed on stage "${stage?.name ?? runCase.currentStageId}" of workflow case ${caseLabel}`);
    }
  }, [runCase, userId, route]);
};

export default useWorkflowRunTrace;
